
import moment, { Moment } from 'moment-timezone';
import pluralize from 'pluralize';
import _isNil from 'lodash/isNil';
import { mapState, mapActions, mapGetters } from 'vuex';
import { functions } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import {
	hundredthsToTokens,
	currencyTokens,
	centsToDollars,
	currency,
	CHECKOUT_RUNNIT_ONE_TIME_TOP_UP_TOKENS,
	CHECKOUT_RUNNIT_ONE_TIME_TOP_UP_TOKENS_TEAM,
	DAILY_FREE_RUNNIT_TOKENS_BASE,
	DAILY_FREE_RUNNIT_TOKENS_CAP,
	DbTimestamp,
	ClubOffer,
	Company,
	BalanceAccount,
	EnterpriseTokenUsage,
	TokenClubAddOn,
	User,
} from '@run-diffusion/shared';
import {
	TeamUserMixin,
	TokenRingMixin,
} from '@/mixins';
import {
	DailyRewardsFooter,
	deriveConsumerStatusPill,
	deriveDailyRewardsFooter,
	deriveMonthlyRefillNote,
	EnterpriseRing,
	EnterpriseUsageSplit,
	splitEnterpriseUsage,
	StatusPill,
	TOKEN_STATUS_HERO_COLOR,
} from '@/utils/tokenBuckets';
import { deriveTopUpPackQuantities } from '@/utils/topUpPacks';
import TokensSVG from '@/assets/TokensSVG.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import TokenCompositionMeter from '@/components/TokenCompositionMeter/TokenCompositionMeter.vue';
import AutoTopUpSettings from '@/components/AutoTopUpSettings/AutoTopUpSettings.vue';

/**
 * The token balance dialog (design handoff: "Token Balance Dialog").
 * One terminology rule: the scheduled subscription grant is a "monthly refill" (consumer) /
 * "monthly reset" (team); "top-up" is reserved exclusively for purchased tokens.
 * Consumer plans count DOWN from an available balance; Enterprise Pro teams count UP against a
 * budget. Non-enterprise Team Plan teams use the consumer layout (they buy like consumers).
 */
export default {
	name: 'RunnitPlanTokensUsageMenuOrBottomSheet',
	components: {
		DialogOrBottomSheet,
		DialogContent,
		TokenCompositionMeter,
		AutoTopUpSettings,
		TokensSVG,
	},
	mixins: [
		TeamUserMixin,
		TokenRingMixin,
	],
	props: {
		menuId: { type: String, default: 'default' },
		/** Optional extra activator listeners. Merged with inner listeners so both fire. */
		mergeTriggerOn: { type: Object, default: null },
		/*
		Describe THIS team instead of the active account. The company teams table opens the dialog
		for a row, handing over the row's team and balance doc. Only enterprise teams are ever pinned:
		the consumer layout below (buy rows, auto top-up) reads the active account from the store, so
		it cannot describe another team's.
		 */
		contextTeam: { type: Object, default: null },
		contextBalanceAccount: { type: Object, default: null },
	},
	data () {
		return {
			DAILY_FREE_RUNNIT_TOKENS_BASE,
			DAILY_FREE_RUNNIT_TOKENS_CAP,
			buyExpanded: false,
			selectedPackQuantity: null,
			// One getRunnitTokensInfo round trip feeds both the upgrade row and auto top-up
			tokensInfo: null as any,
			loadingTokensInfo: false,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
			'userBalanceAccount',
			'teamBalanceAccount',
			'realTimeMoment',
			'upsellDialog',
			'clubOffers',
		]),
		...mapGetters([
			'displayLabels',
		]),
		myRunnitPlanDetailsOpen (): boolean {
			const storeMenuId: string = this._get(this.upsellDialog, 'runnitPlanDetailsData.menuId', 'default');
			const isOpen: boolean = !!this._get(this.upsellDialog, 'runnitPlanDetailsData.open');
			// 'default' is the catch-all for openers that name no menu (the help center, the FAQs). A
			// dialog pinned to a team answers only to its own id, or every table row's would open.
			return isOpen && (storeMenuId === this.menuId || (!this.contextTeam && storeMenuId === 'default'));
		},
		/*
		The team this dialog describes and its balance doc: the pinned one, else the active account's.
		Every team read below goes through these two, and the enterprise figures through `usage` and
		`ring`, which call TokenRingMixin's methods with them.
		 */
		shownTeam (): any {
			return this.contextTeam || this.team;
		},
		shownBalanceAccount (): BalanceAccount {
			return this.contextTeam ? this.contextBalanceAccount : this.teamBalanceAccount;
		},
		usage (): EnterpriseTokenUsage {
			return this.enterpriseUsage(this.shownTeam, this.shownBalanceAccount);
		},
		ring (): EnterpriseRing {
			return this.enterpriseRing(this.shownTeam, this.shownBalanceAccount);
		},
		/*
		OVERRIDES MyBalanceMixin and TokenRingMixin for the shown team. The account picker's
		selection decides these for the active account, and is explicit about TEAM rather than "not
		USER" so an unset selection during init cannot read as an enterprise team; a pinned team is a
		team by definition, whatever the picker says.
		 */
		isTeamAccount (): boolean {
			return !!(this.contextTeam || this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM);
		},
		onPersonalPlan (): boolean {
			return !this.contextTeam && this._get(this.runnitState, 'runnitsOwnerSelection') === RUNNITS_OWNER_SELECTION.USER;
		},
		isEnterprise (): boolean {
			return !!(this.isTeamAccount && this.isEnterpriseTeam(this.shownTeam));
		},
		onTeamPlan (): boolean {
			return !!(this.isTeamAccount && !this.isEnterprise);
		},
		isDeactivatedTeam (): boolean {
			return !!(this.isTeamAccount && this.shownTeam && !this.shownTeam.isActive);
		},
		// TokenRingMixin's isFreemium, under this file's name for it
		isFreemiumConsumer (): boolean {
			return this.isFreemium;
		},
		company (): Company {
			return this.shownTeam && typeof this.shownTeam.company !== 'string' ? this.shownTeam.company : null;
		},
		// TokenRingMixin's dailyCap, under this file's name for it
		dailyTokens (): number {
			return this.dailyCap;
		},
		/*
		Whether this account is actually on the nightly free schedule. calcRunnitTokensDataForBalanceAccount
		forces freeRunnitTokens to 0 without a valid freemium passport, so a Free-plan user WITHOUT one
		gets no daily refill at all — every "refills to N nightly" line is a lie for them, and the used-
		today arithmetic invents usage out of a cap they never had.
		 */
		hasDailyAllowance (): boolean {
			return this.dailyTokens > 0;
		},
		// Its allowance and a rewards link, a prompt to claim the free plan, or nothing. The rules
		// live in deriveDailyRewardsFooter.
		dailyRewardsFooter (): DailyRewardsFooter | null {
			return deriveDailyRewardsFooter({
				onDailyAllowance: this.onDailyAllowance,
				hasDailyAllowance: this.hasDailyAllowance,
				isFreemium: this.isFreemiumConsumer,
				balanceLoading: this.balanceLoading,
			});
		},
		/*
		OVERRIDES TokenRingMixin, deliberately. This dialog reports 0 where the mixin reports a real
		allotment — for a freemium consumer (whose cap is a nightly refill, not a cycle) and for a
		non-enterprise Team Plan (whose top-up flow is not built yet, see the TODO). Anything in the
		mixin that reads cycleAllotment therefore reads THIS while it is mounted here; today that is
		only the ring computeds' consumer branches, which this dialog does not use. The enterprise
		figures here measure against the frozen cycle basis from calcEnterpriseTokenUsage and never
		see this override.
		 */
		cycleAllotment (): number {
			if (this.onPersonalPlan) {
				if (this.isFreemiumConsumer) return 0;
				return this._get(this.user, 'clubInfo.token.tokens') || 0;
			}
			if (this.onTeamPlan) {
				return 0; // TODO, still need to build out all of the auto team plan top up frontend and backend
			}
			return this._get(this.shownTeam, 'allotmentSettings.monthlyRunnitTokensBudget') || 0;
		},
		/*
		Cycle dates
		 */
		renewAtMoment (): Moment {
			const renewAt: DbTimestamp = this.onPersonalPlan
				? (this._get(this.user, 'monthlyBenefitsRenewAt') || null)
				: (this._get(this.company, 'monthlyBenefitsRenewAt') || null);
			if (!renewAt || !renewAt.toMillis) return null;
			const m: Moment = moment(renewAt.toMillis());
			return m.isAfter(moment()) ? m : null;
		},
		refillDateDisplay (): string {
			return this.renewAtMoment ? this.renewAtMoment.format('MMM D') : '';
		},
		refillWhenText (): string {
			if (!this.renewAtMoment) return 'at your next monthly refill';
			const days: number = Math.max(this.renewAtMoment.diff(moment(), 'days'), 0);
			return days > 0
				? `in ${days} ${pluralize('day', days)} on ${this.refillDateDisplay}`
				: `today`;
		},
		topUpExpiryDisplay (): string {
			return this.renewAtMoment ? this.renewAtMoment.clone().add(1, 'month').format('MMM D') : '';
		},
		/*
		Header
		 */
		planTitleText (): string {
			// A deactivated team has no plan to name. Its company can sit on a free club (COMFREE-001 is
			// called "Free Plan"), which would advertise a free team plan that does not exist.
			if (this.isDeactivatedTeam) return `Deactivated ${this.displayLabels.team}`;
			/*
			Always the clubOffer's own name from the database
			*/
			const clubOffer: ClubOffer = this.onPersonalPlan
				? this._get(this.user, 'clubOffer')
				: this._get(this.company, 'clubOffer');
			// clubOffer arrives as an unresolved string ref before it hydrates
			if (!clubOffer || typeof clubOffer === 'string') return 'Plan';
			return clubOffer.name || 'Plan';
		},
		// The team this dialog describes. A personal account has none.
		teamNameText (): string {
			return this.onPersonalPlan ? '' : (this._get(this.shownTeam, 'name') || '');
		},
		/*
		A team account, enterprise or not, leads with the team's name and puts the plan it is on in the
		smaller line beneath. A personal account names only its plan, and so does a team whose name has
		not arrived yet, rather than showing an empty title over a plan name.
		 */
		headerTitleText (): string {
			return this.teamNameText || this.planTitleText;
		},
		headerSubtitleText (): string {
			return this.teamNameText ? this.planTitleText : '';
		},
		showPlanLink (): boolean {
			if (this.isFreemiumConsumer) return false;
			if (this.onPersonalPlan) return true;
			return !!this._get(this.shownTeam, 'isActive');
		},
		planLinkText (): string {
			// 'manage plan' for enterprise, alternate verbiage for everything else
			return this.isEnterprise ? 'manage plan' : 'cancel or change plan';
		},
		/*
		Balance hero. The enterprise figures come from `usage` (calcEnterpriseTokenUsage): the same
		definition the side-nav card, the team dashboard and the company teams table show.
		 */
		heroNumberDisplay (): string {
			if (this.isEnterprise) {
				return currencyTokens(hundredthsToTokens(this.usage.used));
			}
			if (this.onDailyAllowance) {
				return currencyTokens(hundredthsToTokens(this.spendableTokens));
			}
			return currencyTokens(hundredthsToTokens(this.buckets.total));
		},
		statusPill (): StatusPill | null {
			if (this.isDeactivatedTeam) return null;
			if (this.isEnterprise) {
				if (this.usage.isPausedOut) return { text: 'PAUSED', kind: 'pill-danger' };
				if (this.usage.isOver) return { text: 'OVER BUDGET', kind: 'pill-danger' };
				return null;
			}
			if (!this.onPersonalPlan) return null;
			// ringStatus colours the hero number and ringHeld is the figure it displays, so the badge
			// always agrees with the number it sits beside
			return deriveConsumerStatusPill({
				status: this.ringStatus,
				shownTokens: hundredthsToTokens(this.ringHeld),
				isOnDailyFallback: this.isOnDailyFallback,
			});
		},
		subLineText (): string {
			if (this.isDeactivatedTeam) return ''; // No plan, so no reset or refill to promise
			if (this.isEnterprise) {
				if (!this.cycleAllotment) return '';
				const when: string = this.renewAtMoment
					? `resets ${this.refillWhenText.replace('today', 'today')}`
					: 'resets at your next monthly reset';
				return `Monthly budget of ${currencyTokens(hundredthsToTokens(this.cycleAllotment))} ${when}`;
			}
			if (this.onDailyAllowance) {
				if (!this.hasDailyAllowance) return '';
				return `Refill to ${currencyTokens(hundredthsToTokens(this.dailyTokens))} every day around midnight`;
			}
			if (!(this.cycleAllotment > 0)) return '';
			/*
			Only when the refill will add something. Renewal tops the MONTHLY tokens up to the plan amount
			and never removes any, so judging this by the whole balance hid the line from an account with
			10,000 monthly tokens beside an 80,000 top-up, which Oct 12 would still add 50,000 to. An
			account already at the plan amount gets nothing, and the Monthly row says so.
			 */
			if (this.buckets.monthly >= this.cycleAllotment) return '';
			// Only this cycle's top-up carries over. Last cycle's rolled-over top-up expires at the refill,
			// so it must not be promised back
			const rolloverSuffix: string = this.buckets.topUp > 0 ? ' (plus any rollover)' : '';
			const verb: string = this.onTeamPlan ? 'Monthly reset' : 'Monthly refill';
			return `${verb} to ${currencyTokens(hundredthsToTokens(this.cycleAllotment))} ${this.refillWhenText}${rolloverSuffix}`;
		},
		/*
		Meter
		 */
		meterSegments (): any[] {
			if (this.isEnterprise) {
				// Measured against the frozen basis, the same denominator `used` is derived from, so
				// basis + overage always fill the bar exactly (a mid-cycle transfer moves both). That is
				// `used`, plus any debt the cycle opened with, which is overage but not this cycle's usage
				const { basis, used, overage, isOver } = this.usage;
				if (isOver) {
					const total: number = (basis + overage) || 1;
					return [
						{ key: 'budget', fill: 'monthly', widthPct: (basis / total) * 100 },
						{ key: 'overage', fill: 'overage', widthPct: (overage / total) * 100 },
					];
				}
				return [{ key: 'used', fill: 'monthly', widthPct: (used / (basis || 1)) * 100 }];
			}
			if (this.onDailyAllowance) {
				// Without a daily allowance there is nothing to measure the free bucket against, so the
				// bar falls back to the paid balance the account actually holds.
				if (!this.hasDailyAllowance) {
					const paidMax: number = Math.max(this.buckets.total, 1);
					return [{ key: 'paid', fill: 'topup', widthPct: (this.buckets.total / paidMax) * 100 }];
				}
				const barMax: number = Math.max(this.spendableTokens, this.dailyTokens, 1);
				return [
					{ key: 'daily', fill: 'monthly', widthPct: (this.freeTokensHeld / barMax) * 100 },
					{ key: 'paid', fill: 'topup', widthPct: (this.buckets.total / barMax) * 100 },
				];
			}
			const barMax: number = Math.max(this.buckets.total, this.cycleAllotment, 1);
			return [
				{ key: 'rollover', fill: 'rollover', widthPct: (this.buckets.rolledOverTopUp / barMax) * 100 },
				{ key: 'monthly', fill: 'monthly', widthPct: (this.buckets.monthly / barMax) * 100 },
				{ key: 'topup', fill: 'topup', widthPct: (this.buckets.topUp / barMax) * 100 },
			];
		},
		// What the next renewal does to the monthly tokens: how far it refills them, or that it adds nothing
		monthlyRefillNote (): string {
			return deriveMonthlyRefillNote({
				monthly: this.buckets.monthly,
				allotment: this.cycleAllotment,
				dateDisplay: this.refillDateDisplay,
				isTeamPlan: this.onTeamPlan,
			});
		},
		meterLegendRows (): any[] {
			const tokens = (value: number): string => currencyTokens(hundredthsToTokens(value));
			if (this.isEnterprise) {
				/*
				Over budget, the bar splits the total into the budget and the overage, so the legend splits the same
				way: "Used tokens" stops at the budget and "Over budget" carries the rest, and the two add up to the
				"N used" above them. Counting the overage in both rows made them sum past the total (252,399 used
				listed as 252,399 plus 2,399). Debt carried in from last cycle is overage too, but not this cycle's
				usage, so it gets a row of its own (splitEnterpriseUsage).
				 */
				const split: EnterpriseUsageSplit = splitEnterpriseUsage(this.usage);
				const rows: any[] = [
					{ key: 'used', dot: 'monthly', label: 'Used tokens', qualifier: '', valueDisplay: currencyTokens(split.withinBudget), dimmed: !(split.withinBudget > 0) },
				];
				if (this.usage.isOver) {
					if (split.overThisCycle > 0) {
						rows.push({ key: 'over', dot: 'overage', label: 'Over budget', qualifier: '', valueDisplay: currencyTokens(split.overThisCycle), danger: true });
					}
					if (split.carried > 0) {
						rows.push({ key: 'carried', dot: 'overage', label: 'Over budget', qualifier: 'carried over from last cycle', valueDisplay: currencyTokens(split.carried), danger: true });
					}
				} else {
					rows.push({ key: 'remaining', dot: 'track', label: 'Remaining budget', qualifier: this.refillDateDisplay ? `resets ${this.refillDateDisplay}` : '', valueDisplay: tokens(Math.max(this.usage.remaining, 0)) });
				}
				return rows;
			}
			if (this.onDailyAllowance) {
				const rows: any[] = [];
				if (this.hasDailyAllowance) {
					/*
					The nightly refill does not hand out a flat allowance: it tops the COMBINED balance up
					to the cap (see freeRunnitTokensTopUpAmount, which subtracts paid tokens as well as
					free ones). So an account holding paid tokens was granted less than the cap last night,
					and the gap it leaves is not usage. What tonight's refill would grant IS what has been
					spent against the cap, so the same figure answers both, and reading it from the shared
					helper keeps this in step with what the backend actually grants. Measuring the free
					bucket against the cap instead counted an account's paid tokens as tokens it had spent:
					23 free beside 77 paid claimed "77 used today" on a day nothing had been used.
					 */
					const usedToday: number = this._get(this.userBalanceAccountRunnitTokensData, 'freeRunnitTokensTopUpAmount') || 0;
					rows.push({ key: 'daily', dot: 'monthly', label: 'Daily tokens', qualifier: usedToday > 0 ? `${tokens(usedToday)} used today` : '', valueDisplay: `${tokens(this.freeTokensHeld)} / ${tokens(this.dailyTokens)}` });
				}
				/*
				A Free plan has no monthly cycle, so its paid tokens are one line with nothing to break
				down. An account that has fallen through to its daily tokens does have a cycle, so it gets
				the buckets instead: naming the aggregate as well double-counted the same tokens, listing
				"Your tokens 12" above "Monthly tokens 12".

				Both bucket rows carry the paid dot on purpose. This layout spends the light fill on the
				daily allowance, so within it there are two ideas and two colours: light is what the night
				refills, purple is what the account holds, whichever bucket that sits in.
				 */
				if (this.isFreemiumConsumer) {
					if (!this.hasDailyAllowance || this.buckets.total > 0) {
						rows.push({ key: 'paid', dot: 'topup', label: 'Your tokens', qualifier: '', valueDisplay: tokens(this.buckets.total), dimmed: !(this.buckets.total > 0) });
					}
				} else {
					if (this.buckets.rolledOverTopUp > 0) {
						rows.push({
							key: 'rollover',
							dot: 'topup',
							label: 'Rolled over top-up',
							qualifier: this.refillDateDisplay ? `spends first, expires ${this.refillDateDisplay}` : 'spends first, expires at your next refill',
							valueDisplay: tokens(this.buckets.rolledOverTopUp),
						});
					}
					rows.push({ key: 'monthly', dot: 'topup', label: 'Monthly tokens', qualifier: this.monthlyRefillNote, valueDisplay: tokens(this.buckets.monthly), dimmed: !(this.buckets.monthly > 0) });
					rows.push({
						key: 'topup',
						dot: 'topup',
						label: 'Top-up',
						qualifier: this.topUpExpiryDisplay
							? `can roll over into one future cycle (expires ${this.topUpExpiryDisplay})`
							: 'can roll over into one future cycle',
						valueDisplay: tokens(this.buckets.topUp),
						dimmed: !(this.buckets.topUp > 0),
					});
				}
				return rows;
			}
			const rows: any[] = [];
			if (this.buckets.rolledOverTopUp > 0) {
				rows.push({
					key: 'rollover',
					dot: 'rollover',
					label: 'Rolled over top-up',
					qualifier: this.refillDateDisplay ? `spends first, expires ${this.refillDateDisplay}` : 'spends first, expires at your next refill',
					valueDisplay: tokens(this.buckets.rolledOverTopUp),
				});
			}
			rows.push({
				key: 'monthly',
				dot: 'monthly',
				label: 'Monthly tokens',
				qualifier: this.monthlyRefillNote,
				valueDisplay: tokens(this.buckets.monthly),
				dimmed: !(this.buckets.monthly > 0),
			});
			rows.push({
				key: 'topup',
				dot: 'topup',
				// Always describe what a top-up does, whether or not one has been bought: it reads
				// as the bucket's rule rather than a status, and doubles as a nudge when empty.
				label: 'Top-up',
				qualifier: this.topUpExpiryDisplay
					? `can roll over into one future cycle (expires ${this.topUpExpiryDisplay})`
					: 'can roll over into one future cycle',
				valueDisplay: tokens(this.buckets.topUp),
				dimmed: !(this.buckets.topUp > 0),
			});
			return rows;
		},
		/*
		Get more tokens
		 */
		upgradeInfo (): any {
			return this._get(this.tokensInfo, 'upgrade') || null;
		},
		// Skeleton while resolving, the real row once we know an upgrade exists, nothing when maxed out
		showUpgradeSkeleton (): boolean {
			return !!(this.onPersonalPlan && this.loadingTokensInfo && !this.tokensInfo);
		},
		showUpgradeRow (): boolean {
			return !!(this.onPersonalPlan && this._get(this.upgradeInfo, 'available'));
		},
		upgradeSubtitle (): string {
			const monthlyTokens: number = this._get(this.upgradeInfo, 'monthlyTokens') || 0;
			const savings: number = this._get(this.upgradeInfo, 'topUpSavingsPercent') || 0;
			const parts: string[] = [];
			if (monthlyTokens > 0) parts.push(`${currencyTokens(hundredthsToTokens(monthlyTokens))} monthly tokens`);
			// Quote the real number when we have it; only fall back to the vague claim when we don't
			if (savings > 0) parts.push(`top-ups ${savings}% cheaper than your plan`);
			else parts.push('better top-up rate');
			return parts.join(' · ');
		},
		nextPlanTokensDisplay (): string {
			const monthlyTokens: number = this._get(this.upgradeInfo, 'monthlyTokens') || 0;
			return monthlyTokens > 0 ? currencyTokens(hundredthsToTokens(monthlyTokens)) : '';
		},
		nextPlanName (): string {
			return this._get(this.upgradeInfo, 'planName') || '';
		},
		/*
		Identity of what the cached tokensInfo describes. When this changes the payload is stale: the
		upgrade tier, top-up pack, savings percent and payment method all belong to the previous one.

		The SCOPE has to lead. getRunnitTokensInfo answers for exactly one balance account, and
		switching between Personal and a Team swaps which account without touching the user's own club
		or tokenId — so a key built from the plan alone stays identical across that switch, the watcher
		below never fires, and the dialog keeps serving the other account's answer.
		 */
		/*
		Whoever holds the plan: the user for a personal account, the company for a team. Its plan and token
		add-on are what the pack, the upgrade and the auto top-up settings all describe.
		 */
		planHolder (): User | Company | null {
			return this.onPersonalPlan ? this.user : this.company;
		},
		planTokenKey (): string {
			return [
				this.onPersonalPlan ? 'USER' : `TEAM:${this._get(this.shownTeam, 'id') || ''}`,
				this._get(this.planHolder, 'club') || '',
				this._get(this.planHolder, 'clubInfo.tokenId') || '',
			].join('::');
		},
		/*
		The one-time top-up pack is defined on the plan's token add-on, a reference the store resolves on the
		user (personal) or the company (team). So the pack, its price and its limit are on hand with no
		request, and they are the same record checkout charges from. Teams used to have no price here at all.
		 */
		buyPack (): TokenClubAddOn['oneTimeTopUp'] | null {
			return this._get(this.planHolder, 'clubInfo.token.oneTimeTopUp') || null;
		},
		buyUnitTokens (): number {
			return this._get(this.buyPack, 'tokens') || 0;
		},
		buyUnitPriceCents (): number {
			return this._get(this.buyPack, 'stripe.priceAmount') || 0;
		},
		buyMaxQty (): number {
			return this._get(this.buyPack, 'maxQty') || 1;
		},
		showBuyRow (): boolean {
			if (this.onPersonalPlan) {
				return !!(!this.isFreemiumConsumer && this.buyUnitTokens > 0);
			}
			return !!(this.isTeamActive && this.hasManageCompanyPermission && this.buyUnitTokens > 0);
		},
		/*
		Permissions come from three bindings. Until all of them land, TeamUserMixin deliberately reads
		hasManageCompanyPermission as false for everyone, and a null flag means that binding has not even
		started — so without this, every admin would briefly be told they are not one on each open.
		 */
		permissionsResolved (): boolean {
			return [this.loadingUser, this.loadingTeam, this.loadingCompanyUserRole]
				.every((loading: boolean) => !_isNil(loading) && !loading);
		},
		// Only reachable on a non-enterprise team: enterprise and freemium take the other branches
		showAdminOnlyNote (): boolean {
			return !!(!this.onPersonalPlan && this.permissionsResolved && !this.hasManageCompanyPermission);
		},
		buyPacks (): any[] {
			if (!(this.buyUnitTokens > 0)) return [];
			return deriveTopUpPackQuantities({ minQty: 1, maxQty: this.buyMaxQty })
				.map((quantity: number) => ({
					quantity,
					tokensDisplay: currencyTokens(hundredthsToTokens(quantity * this.buyUnitTokens)),
					priceDisplay: this.buyUnitPriceCents > 0 ? currency(centsToDollars(quantity * this.buyUnitPriceCents)) : '',
				}));
		},
		selectedPack (): any {
			return this.buyPacks.find(({ quantity }) => quantity === this.selectedPackQuantity) || null;
		},
		/*
		Amendment 1: the balance numeral takes the status color; the "used" suffix stays muted
		 */
		heroNumberColor (): string {
			// Enterprise takes its ring's status colour, the same the side-nav number wears
			if (this.isEnterprise) return TOKEN_STATUS_HERO_COLOR[this.ring.status] || TOKEN_STATUS_HERO_COLOR.ok;
			// TokenRingMixin's status, so the hero and the sidebar number are never coloured differently
			return TOKEN_STATUS_HERO_COLOR[this.ringStatus] || TOKEN_STATUS_HERO_COLOR.ok;
		},
		/*
		Enterprise policy note (state table from the design's section G)
		 */
		enterprisePolicy (): { icon: string, title: string, detail: string, danger: boolean } {
			const isBound: boolean = !!this._get(this.shownTeam, 'allotmentSettings.isBoundToBudget');
			if (!this._get(this.shownTeam, 'isActive')) {
				return {
					icon: 'mdi-close-circle-outline',
					title: `This ${this.displayLabels.team} was deactivated.`,
					detail: `Please switch to another ${this.displayLabels.team} account or contact your account administrators.`,
					danger: true,
				};
			}
			if (isBound && this.usage.isPausedOut) {
				return {
					icon: 'mdi-cancel',
					title: 'Out of tokens. Generations are paused.',
					detail: this.renewAtMoment
						? `Service resumes at your next monthly reset ${this.refillWhenText}.`
						: 'Service resumes at your next monthly reset.',
					danger: true,
				};
			}
			if (isBound) {
				return {
					icon: 'mdi-wallet-outline',
					title: `This ${this.displayLabels.team} has a fixed monthly token budget.`,
					detail: 'When the budget runs out, generations pause until the next monthly reset.',
					danger: false,
				};
			}
			if (this.usage.isOver) {
				return {
					icon: 'mdi-lock-open-variant-outline',
					title: 'You can keep working without interruption.',
					detail: 'You have gone over budget, and your administrators have been notified. The overage is tracked and settled at your next monthly reset.',
					danger: true,
				};
			}
			return {
				icon: 'mdi-lock-open-variant-outline',
				title: 'Usage continues after tokens run out.',
				detail: 'You are allowed to go over budget and any overage is tracked and settled at your next monthly reset.',
				danger: false,
			};
		},
	},
	watch: {
		myRunnitPlanDetailsOpen (isOpen: boolean): void {
			if (!isOpen) {
				this.buyExpanded = false;
				this.selectedPackQuantity = null;
			} else {
				this.loadTokensInfo();
			}
		},
		planTokenKey (newKey: string, oldKey: string): void {
			/*
			The user changed plan or token add-on (an upgrade/downgrade completing, or a change made
			in another tab). Everything cached describes the old tier, so drop it: refetch now if the
			dialog is open, otherwise let the next open reload it.
			 */
			if (newKey === oldKey) return;
			this.tokensInfo = null;
			// A request still in flight is for the old key, and its finally only releases the flag for
			// the current one. Release it here, or the next open bails at the loading check for good.
			this.loadingTokensInfo = false;
			// A pack from the old plan's pricing shouldn't stay selected
			this.selectedPackQuantity = null;
			if (this.myRunnitPlanDetailsOpen) {
				this.loadTokensInfo(true);
			}
		},
	},
	methods: {
		...mapActions([
			'setUpsellDialog',
		]),
		mergedTriggerOn (baseOn: Record<string, unknown>) {
			const extra = this.mergeTriggerOn as Record<string, (...args: unknown[]) => void> | null | undefined;
			if (!extra || typeof extra !== 'object') return baseOn;
			const out: Record<string, unknown> = { ...baseOn };
			Object.keys(extra).forEach((key: string) => {
				const e = extra[key];
				const b = baseOn[key];
				if (typeof b === 'function' && typeof e === 'function') {
					out[key] = (...args: unknown[]) => {
						(e as (...args: unknown[]) => void)(...args);
						(b as (...args: unknown[]) => void)(...args);
					};
				} else if (typeof e === 'function') {
					out[key] = e;
				}
			});
			return out;
		},
		handleMenuStateChange (isOpen: boolean) {
			this.setUpsellDialog({
				runnitPlanDetailsData: {
					open: !!isOpen,
					...(this.menuId && { menuId: this.menuId }),
				},
			});
		},
		onManagePlanLinkClick () {
			this.handleMenuStateChange(false);
			// Opened from the company teams table: the plan to manage is that team's own page
			if (this.contextTeam) {
				this.routerPush(this.$route, this.$router, {
					name: ROUTER.TEAM_DASHBOARD,
					params: { teamId: this.contextTeam.id },
				});
				return;
			}
			this.routerPush(this.$route, this.$router, {
				name: this.onPersonalPlan ? ROUTER.RUNNITS_SUBSCRIPTIONS : ROUTER.COMPANY_DASHBOARD,
			});
		},
		goToUpgrade (close) {
			if (close) close();
			this.handleMenuStateChange(false);
			this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_SUBSCRIPTIONS });
		},
		goToReferrals (close) {
			if (close) close();
			this.handleMenuStateChange(false);
			this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_REFERRALS });
		},
		onToggleBuyExpanded () {
			this.buyExpanded = !this.buyExpanded;
			if (!this.buyExpanded) this.selectedPackQuantity = null;
		},
		onBuyConfirm () {
			// Checkout redirect (in-dialog one-click purchase is a later phase). The picked pack's
			// quantity rides the query as `qty` so checkout opens preloaded with it.
			if (!this.selectedPack) return;
			const routeData = this.$router.resolve({
				name: 'GoToPayment',
				query: {
					checkoutAction: this.onPersonalPlan
						? CHECKOUT_RUNNIT_ONE_TIME_TOP_UP_TOKENS
						: CHECKOUT_RUNNIT_ONE_TIME_TOP_UP_TOKENS_TEAM,
					qty: String(this.selectedPack.quantity),
				},
			});
			window.open(routeData.href, '_blank');
		},
		async loadTokensInfo (force: boolean = false): Promise<void> {
			/*
			Single source for the upgrade row and the auto top-up section. Resolving the upgrade
			server-side is what lets us tell "there is a bigger plan" apart from "this account is
			maxed out" — the client can't see unpublished/other-type offers or price-per-token
			across clubs, so it used to advertise a vague upgrade that may not have existed.
			 */
			// Enterprise teams get tokens through their account manager: the server answers them with no
			// upgrade and no auto top-up, and the enterprise branch renders neither section anyway.
			if (this.isEnterprise) {
				this.tokensInfo = null;
				return;
			}
			if (!force && (this.tokensInfo || this.loadingTokensInfo)) return;
			if (!this.onPersonalPlan && !this.hasManageCompanyPermission) return;
			// The payload only describes the plan it was requested for, so the plan key IS the
			// request's identity: if it no longer matches on arrival, the answer is for a plan
			// the user has since left.
			const requestKey: string = this.planTokenKey;
			this.loadingTokensInfo = true;
			try {
				const functionRef = functions.httpsCallable('getRunnitTokensInfo');
				const { success, message, info } = (await functionRef({
					balanceAccountType: this.onPersonalPlan ? 'USER' : 'TEAM',
					...(!this.onPersonalPlan && { teamId: this._get(this.shownTeam, 'id') }),
				})).data;
				if (requestKey !== this.planTokenKey) return;
				if (!success) throw new Error(message || 'Error loading token info');
				this.tokensInfo = info;
			} catch (e) {
				if (requestKey !== this.planTokenKey) return;
				console.error('Error loading token info', e);
			} finally {
				if (requestKey === this.planTokenKey) {
					this.loadingTokensInfo = false;
				}
			}
		},
		onEnterpriseTransferTokensLearnMore () {
			window.open('https://www.rundiffusion.com/how-to-transfer-tokens-on-runnit', '_blank');
		},
		onEnterprisePurchaseTokensLearnMore () {
			window.open('https://www.rundiffusion.com/how-to-top-up-tokens-on-an-enterprise-account', '_blank');
		},
	},
};
